import { ApiRequestError } from '@/shared/lib/api';
import { isApiValidationError } from '@/shared/lib/api-validation-errors';
import { messages } from '@/shared/i18n';

/**
 * Normalised shape that feeds both our toast host and any inline banners.
 * `title` is a short headline; `body` is the full explanation.
 */
export interface ToastErrorPayload {
  title: string;
  body: string;
}

/** Map of backend `error.code` values to default UI copy. */
export type ErrorCodeMap = Record<string, ToastErrorPayload>;

/**
 * Default copy per backend error code. The backend's human `error.message`
 * always takes precedence over the default `body` below, so this acts as a
 * safety net when the server sends only a code.
 *
 * Keep these keys in sync with `salaamah-backend/api/constants.py::ErrorCode`.
 */
const DEFAULT_MAP: ErrorCodeMap = {
  bad_request: {
    title: messages.errors.genericTitle,
    body: messages.errors.genericBody,
  },
  validation_error: {
    title: messages.errors.validationTitle,
    body: messages.errors.validationBody,
  },
  unauthorized: {
    title: messages.errors.unauthorizedTitle,
    body: messages.errors.unauthorizedBody,
  },
  invalid_token: {
    title: messages.errors.invalidTokenTitle,
    body: messages.errors.invalidTokenBody,
  },
  token_expired: {
    title: messages.errors.tokenExpiredTitle,
    body: messages.errors.tokenExpiredBody,
  },
  conflict: {
    title: messages.errors.conflictTitle,
    body: messages.errors.conflictBody,
  },
  not_found: {
    title: messages.errors.notFoundTitle,
    body: messages.errors.notFoundBody,
  },
  internal_server_error: {
    title: messages.errors.serverTitle,
    body: messages.errors.serverBody,
  },
  parse_error: {
    title: messages.errors.serverTitle,
    body: messages.errors.serverBody,
  },
};

/**
 * Resolve a thrown error into the `{ title, body }` pair we render in toasts.
 *
 * Resolution order:
 *   1. Caller-supplied `overrides[err.code]` (per-feature copy).
 *   2. Default map entry for `err.code`.
 *   3. The `fallback` the caller provides for their feature.
 *
 * For the body we prefer the backend's human message (`err.message`) when
 * present, except for `validation_error` where specifics live in
 * `error.details` and the top-level message is always generic.
 */
export function resolveErrorToast(
  err: unknown,
  fallback: ToastErrorPayload,
  overrides?: ErrorCodeMap,
): ToastErrorPayload {
  if (err instanceof ApiRequestError) {
    const match = overrides?.[err.code] ?? DEFAULT_MAP[err.code];
    const title = match?.title ?? fallback.title;
    if (isApiValidationError(err)) {
      return {
        title: match?.title ?? messages.errors.validationTitle,
        body: match?.body ?? messages.errors.validationBody,
      };
    }
    // Prefer the backend's human message; fall back through defaults when empty.
    // `||` (not `??`) is intentional: we want empty strings to trigger fallback.
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const body = err.message || match?.body || fallback.body;
    return { title, body };
  }
  return fallback;
}
